import { useCallback, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import { patchUser } from 'api';
import { useSnackContext } from 'hooks/useSnackContext';
import { UserProps } from 'types';

interface UserManagementActionsProps {
  user_url: string;
  user: UserProps;
  setUser: React.Dispatch<React.SetStateAction<UserProps>>;
}
export const UserManagementActions = (props: UserManagementActionsProps) => {
  const { user, setUser } = props;

  const [activating, setActivating] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const { setSnack } = useSnackContext();
  const { id, account_status } = user;

  const handleMakeUserDisabled = useCallback(async () => {
    try {
      setDisabling(true);
      const { data } = await patchUser(id, {
        data: {
          account_status: 'disabled',
        },
      });
      setDisabling(false);
      setUser((prevUser) => ({ ...prevUser, ...data }));
      setSnack((props) => ({
        ...props,
        severity: 'success',
        open: true,
        message: 'User Is Disabled',
      }));
    } catch (e) {
      setDisabling(false);
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'User Was Not Set To Disabled',
        error: e as Error,
      }));
    }
  }, [id, setSnack, setUser]);

  const handleEnableUser = useCallback(async () => {
    try {
      setEnabling(true);
      const { data } = await patchUser(id, {
        data: {
          account_status: 'active',
        },
      });
      setEnabling(false);
      setUser((prevUser) => ({ ...prevUser, ...data }));
    } catch (e) {
      setEnabling(false);
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'User Was Not Enabled',
        error: e as Error,
      }));
    }
  }, [id, setSnack, setUser]);

  const handleMakeUserActive = useCallback(async () => {
    try {
      setActivating(true);
      const { data } = await patchUser(id, {
        data: {
          account_status: 'active',
        },
      });
      setUser((prevUser) => ({ ...prevUser, ...data }));
      setSnack((props) => ({
        ...props,
        severity: 'success',
        open: true,
        message: 'User Is Active',
      }));
    } catch (e) {
      setActivating(false);
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'User Was Not Made Active',
        error: e as Error,
      }));
    }
    setActivating(false);
  }, [id, setSnack, setUser]);

  const renderActions = (account_status: string) => {
    switch (account_status) {
      case 'active':
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
            }}
          >
            <LoadingButton
              loading={disabling}
              sx={{ ml: 1 }}
              onClick={handleMakeUserDisabled}
              variant='text'
            >
              Administrative DISABLE
            </LoadingButton>
          </Box>
        );
      case 'disabled':
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
            }}
          >
            <LoadingButton
              loading={enabling}
              sx={{ ml: 1 }}
              onClick={handleEnableUser}
              variant='text'
            >
              Administrative ENABLE
            </LoadingButton>
          </Box>
        );
      case 'inactive':
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
            }}
          >
            <LoadingButton
              loading={activating}
              sx={{ float: 'right' }}
              onClick={handleMakeUserActive}
              variant='text'
            >
              ACTIVATE USER
            </LoadingButton>
          </Box>
        );

      default:
        return '';
    }
  };

  return <>{renderActions(account_status)}</>;
};
